/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/delete 目录；
 * 2. 负责根据 Worker 命令历史、结果对象与草图投影计算特征删除影响范围；
 * 3. 不执行 Viewer mutation，供 Viewer 删除门面、确认框和 Undo/Redo 历史共同消费。
 */

import {buildPartFeatureDocument} from '../projection/FeatureTreeProjectionBuilder';
import {
    FeatureCommandDependencyIndex,
    type FeatureCommandDependencyEntry,
} from '../command/FeatureCommandDependencyIndex';
import {createFeatureCommandSeq} from '../rebuild/FeatureReplayConsistency';
import {
    createSketchIdentityIndex,
    normalizeSketchIdToRuntime,
} from '../../sketch/identity/sketchIdentityIndex';
import type {
    FeatureDeletionAbsorbedSketchSummary,
    FeatureDeletionFeatureSummary,
    FeatureDeletionPlan,
    ResolveFeatureDeletionPlanInput,
} from './FeatureDeletionTypes';

/**
 * 克隆命令历史，避免调用方后续误改删除计划中的权威序列。
 *
 * @param commands 命令历史。
 * @returns 克隆后的命令历史。
 */
function cloneCommands(commands: readonly CadCommand[]): CadCommand[] {
    return commands.map((command) => structuredClone(command));
}

/**
 * 特征删除计划器。
 *
 * 设计说明：
 * 1. 该类只负责根据 CAD 主状态计算删除影响范围，不执行 Viewer mutation；
 * 2. 设计树、确认框和 Undo/Redo 都消费同一份计划，避免 UI 层重复推导依赖；
 * 3. 当前实现对齐 SolidWorks Basic 删除语义：删除目标特征及其下游 child features，
 *    仅恢复目标特征自身的来源草图，下游子特征来源草图随子特征删除，避免脱离已删除子特征后泄漏为顶级草图。
 */
export class FeatureDeletionPlanner {
    /**
     * 解析特征删除计划。
     *
     * @param input 删除计划输入。
     * @returns 删除计划；目标不是有效特征时返回 `null`。
     */
    public resolvePlan(input: ResolveFeatureDeletionPlanInput): FeatureDeletionPlan | null {
        const dependencyIndex = new FeatureCommandDependencyIndex({
            commands: input.commands,
            objects: input.objects,
        });
        /**
         * 草图身份归一索引。
         *
         * 特征命令记录的来源草图是持久 ID，而删除计划输出的草图 ID 会直接驱动场景草图组移除、
         * 显示态恢复与设计树摘要，这些消费点只认运行时 uuid。因此条目一进入计划器就统一归一，
         * 后续所有草图集合运算都在同一个 ID 空间内完成。
         */
        const sketchIdentityIndex = createSketchIdentityIndex(
            input.sceneSketches.map((sketch) => ({
                runtimeId: sketch.id,
                persistentId: sketch.persistentId,
            })),
        );
        const featureEntries = this.normalizeEntrySourceSketchIds(
            dependencyIndex.getEntries(),
            sketchIdentityIndex,
        );
        const targetEntry = featureEntries.find((entry) => entry.command.id === input.targetFeatureId) ?? null;

        if (!targetEntry) {
            return null;
        }

        const document = buildPartFeatureDocument({
            history: cloneCommands(input.commands),
            objects: [...input.objects],
            sceneSketches: [...input.sceneSketches],
            hiddenObjectIds: new Set(input.hiddenObjectIds),
            suppressedSketchIds: new Set(input.suppressedSketchIds),
            objectNameOverrides: new Map(input.objectNameOverrides),
        });
        const deletedFeatureIds = dependencyIndex.resolveDependentFeatureIds(targetEntry.command.id);
        const deletedFeatureIdSet = new Set(deletedFeatureIds);
        const remainingCommands = cloneCommands(input.commands.filter((command) => !deletedFeatureIdSet.has(command.id)));
        const affectedSketchIds = this.resolveAffectedSketchIds(deletedFeatureIds, featureEntries);
        const restoredSketchIds = this.resolveRestoredSketchIds(
            targetEntry,
            affectedSketchIds,
            remainingCommands,
            sketchIdentityIndex,
        );
        const deletedSketchIds = this.resolveDeletedSketchIds({
            affectedSketchIds,
            restoredSketchIds,
            remainingCommands,
            sceneSketches: input.sceneSketches,
            sketchIdentityIndex,
        });
        const targetFeature = this.createFeatureSummary(targetEntry, document);
        const childFeatures = deletedFeatureIds
            .filter((featureId) => featureId !== targetEntry.command.id)
            .map((featureId) => {
                const entry = featureEntries.find((candidate) => candidate.command.id === featureId);
                if (!entry) {
                    throw new Error(`删除计划中存在未知子特征：${featureId}`);
                }

                return this.createFeatureSummary(entry, document);
            });

        return {
            targetFeature,
            commandSeq: createFeatureCommandSeq(input.commands),
            deletedFeatureIds,
            childFeatures,
            absorbedSketches: this.createAbsorbedSketchSummaries(affectedSketchIds, restoredSketchIds, document),
            restoredSketchIds,
            deletedSketchIds,
            affectedSketchIds,
            remainingCommands,
            rollbackCommands: cloneCommands(input.commands),
            requiresConfirmation: childFeatures.length > 0,
        };
    }

    /**
     * 收集命令历史中的特征条目，并把来源草图归一为场景运行时句柄。
     *
     * @param commands 命令历史。
     * @param objects 当前几何对象摘要。
     * @param sketchIdentityIndex 草图身份归一索引。
     * @returns 特征条目集合。
     */
    private collectFeatureEntries(
        commands: readonly CadCommand[],
        objects: readonly GeometryObject[],
        sketchIdentityIndex: ReadonlyMap<string, string>,
    ): FeatureCommandDependencyEntry[] {
        return this.normalizeEntrySourceSketchIds(
            new FeatureCommandDependencyIndex({commands, objects}).getEntries(),
            sketchIdentityIndex,
        );
    }

    /**
     * 把特征条目的来源草图统一归一为场景运行时句柄。
     *
     * @param entries 原始特征条目。
     * @param sketchIdentityIndex 草图身份归一索引。
     * @returns 归一后的特征条目。
     */
    private normalizeEntrySourceSketchIds(
        entries: readonly FeatureCommandDependencyEntry[],
        sketchIdentityIndex: ReadonlyMap<string, string>,
    ): FeatureCommandDependencyEntry[] {
        return entries.map((entry) => ({
            ...entry,
            sourceSketchId: normalizeSketchIdToRuntime(sketchIdentityIndex, entry.sourceSketchId),
        }));
    }

    /**
     * 解析被删除特征影响到的来源草图。
     *
     * @param deletedFeatureIds 待删除特征 ID。
     * @param featureEntries 全部特征条目。
     * @returns 去重后的草图 ID。
     */
    private resolveAffectedSketchIds(
        deletedFeatureIds: readonly string[],
        featureEntries: readonly FeatureCommandDependencyEntry[],
    ): string[] {
        const deletedFeatureIdSet = new Set(deletedFeatureIds);
        const sketchIds = new Set<string>();

        featureEntries.forEach((entry) => {
            if (!deletedFeatureIdSet.has(entry.command.id) || !entry.sourceSketchId) {
                return;
            }

            sketchIds.add(entry.sourceSketchId);
        });

        return Array.from(sketchIds);
    }

    /**
     * 解析删除完成后需要重新显示的来源草图。
     *
     * @param targetEntry 用户直接删除的目标特征。
     * @param affectedSketchIds 被删除特征影响的草图 ID。
     * @param remainingCommands 删除后的命令历史。
     * @param sketchIdentityIndex 草图身份归一索引。
     * @returns 需要恢复为显示态的草图 ID。
     */
    private resolveRestoredSketchIds(
        targetEntry: FeatureCommandDependencyEntry,
        affectedSketchIds: readonly string[],
        remainingCommands: readonly CadCommand[],
        sketchIdentityIndex: ReadonlyMap<string, string>,
    ): string[] {
        const targetSourceSketchId = targetEntry.sourceSketchId;
        if (!targetSourceSketchId || !affectedSketchIds.includes(targetSourceSketchId)) {
            return [];
        }

        const remainingFeatureEntries = this.collectFeatureEntries(remainingCommands, [], sketchIdentityIndex);
        const isTargetSketchStillConsumed = remainingFeatureEntries
            .some((entry) => entry.sourceSketchId === targetSourceSketchId);

        /**
         * 删除父特征时，下游子特征也会被删除。子特征自己的来源草图属于被删除子特征的吸收关系，
         * 不能因为命令历史为空就恢复成顶级草图；否则会出现“切除已删除但草图仍留在树和场景里”的不一致状态。
         */
        return isTargetSketchStillConsumed ? [] : [targetSourceSketchId];
    }

    /**
     * 解析随子特征一起删除的来源草图。
     *
     * @param input 删除草图解析输入。
     * @returns 需要移除运行时草图组的草图 ID。
     */
    private resolveDeletedSketchIds(input: {
        readonly affectedSketchIds: readonly string[];
        readonly restoredSketchIds: readonly string[];
        readonly remainingCommands: readonly CadCommand[];
        readonly sceneSketches: ResolveFeatureDeletionPlanInput['sceneSketches'];
        readonly sketchIdentityIndex: ReadonlyMap<string, string>;
    }): string[] {
        const restoredSketchIdSet = new Set(input.restoredSketchIds);
        const sceneSketchIdSet = new Set(input.sceneSketches.map((sketch) => sketch.id));
        const remainingConsumedSketchIds = new Set(
            this.collectFeatureEntries(input.remainingCommands, [], input.sketchIdentityIndex)
                .map((entry) => entry.sourceSketchId)
                .filter((sketchId): sketchId is string => typeof sketchId === 'string' && sketchId.length > 0),
        );

        return input.affectedSketchIds.filter((sketchId) => {
            if (!sceneSketchIdSet.has(sketchId)) {
                return false;
            }
            if (restoredSketchIdSet.has(sketchId)) {
                return false;
            }

            return !remainingConsumedSketchIds.has(sketchId);
        });
    }

    /**
     * 创建特征删除摘要。
     *
     * @param entry 特征命令条目。
     * @param document 设计树投影文档。
     * @returns 特征摘要。
     */
    private createFeatureSummary(
        entry: FeatureCommandDependencyEntry,
        document: ReturnType<typeof buildPartFeatureDocument>,
    ): FeatureDeletionFeatureSummary {
        const feature = document.features.find((candidate) => candidate.id === entry.command.id);

        return {
            featureId: entry.command.id,
            featureKind: feature?.kind ?? entry.featureKind,
            displayName: feature?.displayName ?? entry.command.id,
            commandIndex: entry.commandIndex,
            sourceSketchId: entry.sourceSketchId ?? undefined,
        };
    }

    /**
     * 创建被吸收草图摘要。
     *
     * @param affectedSketchIds 受影响草图 ID。
     * @param restoredSketchIds 将恢复为顶级显示的草图 ID。
     * @param document 设计树投影文档。
     * @returns 被吸收草图摘要。
     */
    private createAbsorbedSketchSummaries(
        affectedSketchIds: readonly string[],
        restoredSketchIds: readonly string[],
        document: ReturnType<typeof buildPartFeatureDocument>,
    ): FeatureDeletionAbsorbedSketchSummary[] {
        const restoredSketchIdSet = new Set(restoredSketchIds);

        return affectedSketchIds.map((sketchId) => {
            const sketch = document.sketches.find((candidate) => candidate.id === sketchId);

            return {
                sketchId,
                displayName: sketch?.displayName ?? sketchId,
                restoredAsTopLevel: restoredSketchIdSet.has(sketchId),
            };
        });
    }
}
